/**
 * Routing helpers (Martin). The PWA can't bind to a specific native maps app,
 * so we use the universal Google Maps directions URL which the OS hands off to
 * the installed maps app (Android intent / iOS "Open in Maps"), falling back to
 * the browser. For in-app directions we use the classic embed (no API key).
 */

export interface MapsDestination {
  name?: string | null;
  address?: string | null;
  city?: string | null;
}

export interface MapsOrigin {
  latitude: number;
  longitude: number;
}

/** Address string used as the routing destination. */
export function destinationQuery(d: MapsDestination): string {
  return [d.address, d.city]
    .map((p) => p?.trim())
    .filter((p): p is string => !!p && p.length > 0)
    .join(', ');
}

export function hasDestination(d: MapsDestination): boolean {
  return destinationQuery(d).length > 0;
}

/** iPhone / iPad (Safari or installed PWA). */
export function isAppleMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Apple Maps directions — on iOS this opens the Maps app (or a separate Safari
 * tab) so the driver app is not replaced in the same window.
 */
export function buildAppleMapsUrl(dest: MapsDestination): string {
  const params = new URLSearchParams({
    daddr: destinationQuery(dest),
    dirflg: 'd',
  });
  return `https://maps.apple.com/?${params.toString()}`;
}

/**
 * Navigate URL — Google Maps directions, opened via `<a target="_blank">` so the
 * driver app tab is not replaced (Martin feedback v1.0.7).
 */
export function buildNavigateUrl(dest: MapsDestination): string {
  return buildDirectionsUrl(dest);
}

/**
 * Universal Google Maps directions URL. Opens the device's maps app when one is
 * installed, otherwise the browser. Origin is the driver's current GPS fix when
 * available; otherwise the maps app uses the device's current location.
 */
export function buildDirectionsUrl(dest: MapsDestination, origin?: MapsOrigin | null): string {
  const params = new URLSearchParams({
    api: '1',
    destination: destinationQuery(dest),
    travelmode: 'driving',
  });
  if (origin) params.set('origin', `${origin.latitude},${origin.longitude}`);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Embeddable directions map (classic Google Maps embed, no API key required). */
export function buildEmbedUrl(dest: MapsDestination, origin?: MapsOrigin | null): string {
  const daddr = encodeURIComponent(destinationQuery(dest));
  const saddr = origin ? `${origin.latitude},${origin.longitude}` : '';
  return `https://maps.google.com/maps?saddr=${saddr}&daddr=${daddr}&output=embed`;
}
